
<script>
    import pusherService from '../services/PusherService';
    
    const RepeatPrescriptionService = {}

    RepeatPrescriptionService.install = function (Vue) {

        var repeatPrescriptionService = new Object ();

        Object.defineProperty(Vue.prototype, '$repeatPrescriptionService', { value: repeatPrescriptionService });

        // Every repeat-prescription call goes through this base so a backend
        // move/rename is a one-line change here, or a VUE_APP_REPEAT_API_BASE
        // override in .env.local. The admin-scoped mirror of the doctor portal's
        // repeat-prescription endpoints; admin tokens are rejected on api/doctor/*.
        Object.defineProperty(repeatPrescriptionService, 'baseUrl', {
            get: function () {
                return process.env.VUE_APP_REPEAT_API_BASE || 'api/admin/repeat-prescription';
            }
        });

        // Responses come wrapped in a { data, message, status } envelope.
        // Backend timestamps are ZonedDateTime strings that can carry a trailing
        // zone id ("...+01:00[Europe/London]") which moment cannot parse.
        var stripZoneId = function (timestamp) {
            return (timestamp || '').replace(/\[.*\]$/, '');
        };

        var normalise = function (item) {
            if (!item) {
                return item;
            }
            if (item.paymentAttempts) {
                item.paymentAttempts = item.paymentAttempts.map(attempt =>
                    Object.assign({}, attempt, { attemptTime: stripZoneId(attempt.attemptTime) })
                ).sort((a, b) => a.attemptNo - b.attemptNo);
            }
            if (item.orderRepeatPRSCFlowDetails) {
                item.orderRepeatPRSCFlowDetails = item.orderRepeatPRSCFlowDetails.slice()
                    .sort((a, b) => a.flowCount - b.flowCount);
            }
            return item;
        };

        repeatPrescriptionService.listRepeatPrescriptions = function (stage = 'ALL') {
            return Vue.axios.get(this.baseUrl, { headers: { stage: stage } })
            .then(response => {
                var rows = (response.data && response.data.data) || [];
                return rows.map(normalise).sort((a, b) =>
                    String(a.nextScheduledDate || '').localeCompare(String(b.nextScheduledDate || '')));
            })
            .catch(error => {
                this.handleError(error)
                return Promise.reject(error);
            });
        };

        repeatPrescriptionService.getRepeatPrescriptionDetail = function (orderItemId) {
            return Vue.axios.get(this.baseUrl + '/' + orderItemId)
            .then(response => { return normalise((response.data && response.data.data) || null); })
            .catch(error => {
                this.handleError(error)
                return Promise.reject(error);
            });
        };

        // Manual charge against the saved card. The backend performs the whole
        // capture server-side (token, setup-intent, preauth) with the correct
        // repeat pricing, stamps the attempt attemptedBy=ADMIN, and rejects when
        // the patient has no saved card or the cycle is outside its 7-day review
        // window. The capture outcome is asynchronous; it surfaces later through
        // the cycle's paymentAttempts.
        repeatPrescriptionService.retryPaymentNow = function (orderItemId) {
            return Vue.axios.post(this.baseUrl + '/payment/process/' + orderItemId)
            .then(response => { return response.data; })
            .catch(error => {
                this.handleError(error)
                return Promise.reject(error);
            });
        };

        // Cancels an active repeat cycle (processStatus ACTIVE). Sends patient notification via backend.
        repeatPrescriptionService.cancelRepeatPrescription = function (orderItemId, cancellationReason) {
            return Vue.axios.put(this.baseUrl + '/cancel', {
                orderItemId: orderItemId,
                cancellationReason: cancellationReason
            })
            .then(response => { return response.data; })
            .catch(error => {
                this.handleError(error);
                return Promise.reject(error);
            });
        };

        var awaitPreAuthToken = function (orderItemId) {
            var unsubscribe = null;
            return new Promise((resolve, reject) => {
                var settled = false;
                unsubscribe = pusherService.listen('pre-auth-token', 'token', data => {
                    if (data && data.preAuthToken && data.orderItemId == orderItemId) { 
                        settled = true;
                        unsubscribe();
                        resolve(data.preAuthToken);
                    }
                });
                setTimeout(() => {
                    if (!settled) {
                        unsubscribe();
                        reject(new Error('Timed out waiting for the payment authorisation token'));
                    }
                }, 30000);
            });
        };

        repeatPrescriptionService.sendPaymentUpdateLink = function (orderItemId, patientEmail, patientId, documentType = 'prescription') {
            const tokenPromise = awaitPreAuthToken(orderItemId);

            tokenPromise.catch(() => {});
            return this.getRepeatPrescriptionDetail(orderItemId)
            .then(prescriptionDetail => {
                const deliveryType = prescriptionDetail?.deliveryType;
                const prescriptionId = prescriptionDetail?.prescriptionId;
                const medicationFee = prescriptionDetail?.medicationPrice;
                
                // Repeat prescription fee from Pricing (GPS); £4.99 if it can't be loaded.
                const repeatFeePromise = Vue.prototype.$pricingService
                    ? Vue.prototype.$pricingService.gpsPrice('repeatPrescriptionFee', 4.99)
                    : Promise.resolve(4.99);
                return Vue.prototype.$orderService.getPreAuthToken(patientEmail, orderItemId)
                .then(() => tokenPromise)  
                .then(token => {
                    return Promise.all([Vue.prototype.$orderService.getAdditionalPaymentStatus(orderItemId), repeatFeePromise])
                        .then(([response, documentFee]) => {
                            const paymentData = response?.data || {};
                            const deliveryFee = paymentData.deliveryFee || 0;
                            
                            return { token, documentFee, medicationFee, deliveryFee, deliveryType,  };
                        })
                        .catch(() => {
                            return { token, documentFee: 0, medicationFee: 0, deliveryFee: 0, deliveryType,  };
                        });
                })
                .then(({ token, documentFee, deliveryFee, medicationFee, deliveryType }) => {
                    const isPhysical = deliveryType === 'PHARMACY';
                    const totalCharge = isPhysical ? documentFee : (documentFee + medicationFee + deliveryFee);
                    
                    const payload = {
                        message: "Repeat Prescription Payment Request",
                        emailTemplateName: "prescription-order-summary-payment.html",
                        subject: "Repeat Prescription Payment Update",
                        isSmsEnable: false,
                        isWhatsAppEnable: false,
                        isEmailEnable: true,
                        type: "REPEAT-PRESCRIPTION-PAYMENT-REQUEST",
                        url1: `${process.env.VUE_APP_PATIENT_URL}repeat-prescription/${orderItemId}/${patientId}/${prescriptionId}/${token}`,
                        orderItemId: orderItemId,
                        documentType: documentType,
                        deliveryMethod: deliveryType,
                        additionalServiceFee: documentFee,
                        medicationFee: medicationFee,
                        deliveryFee: deliveryFee,
                        totalCharge: totalCharge,
                        addressType: "DELIVERY",
                    };

                    return Vue.prototype.$orderService.resendDocumentConsent(payload);
                })
                .then(response => { 
                    return response.data; 
                })
                .catch(error => {
                    this.handleError(error);
                    return Promise.reject(error);
                });
            });
        };

        repeatPrescriptionService.handleError = function(error) {
            if (error.response) {
              // The request was made and the server responded with a status code that falls out of the range of 2xx
              console.debug(error.response.data);
              console.debug(error.response.status);
              console.debug(error.response.headers);
              return {
                success: false,
                status: error.response.status,
                message: error.response.message,
              }
            } else if (error.request) {
              // The request was made but no response was received `error.request` is an instance of XMLHttpRequest in the browser and an instance of http.ClientRequest in node.js
              console.debug('error.request ', error.request);
              return {
                success: false,
                message: 'Did not receive a response from the server.',
              }
            } else {
              // Something happened in setting up the request that triggered an Error
              console.debug('error', error.request);
              return {
                success: false,
                message: 'Failed to make the request.',
              }
            }
        };
    }

    export default RepeatPrescriptionService;
</script>
